import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { useDnsRecordMutations } from "@/features/dns/hooks/useDns";
import type { IDnsRecord } from "@/features/dns/schemas/dns";
import {
  dnsEditFormSchema,
  type DnsEditFormInput,
  type DnsEditFormValues,
} from "@/features/dns/schemas/dns";

interface DnsEditRecordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  record: IDnsRecord | null;
  mutations: ReturnType<typeof useDnsRecordMutations>;
}

export function DnsEditRecordDialog({
  open,
  onOpenChange,
  record,
  mutations,
}: DnsEditRecordDialogProps) {
  const form = useForm<DnsEditFormInput, unknown, DnsEditFormValues>({
    resolver: zodResolver(dnsEditFormSchema),
    defaultValues: {
      name: "",
      type: "",
      content: "",
      ttl: 120,
      proxied: false,
      comment: "",
    },
  });
  const { reset, handleSubmit, register } = form;

  useEffect(() => {
    if (record && open) {
      reset({
        name: record.name,
        type: record.type,
        content: record.content,
        ttl: record.ttl,
        proxied: Boolean(record.proxied),
        comment: record.comment ?? "",
      });
    }
  }, [record, open, reset]);

  const onSubmit = (values: DnsEditFormValues) => {
    if (!record) return;
    const body: Record<string, unknown> = {
      name: values.name,
      type: values.type,
      content: values.content,
      ttl: values.ttl,
      ...(values.proxied !== undefined ? { proxied: values.proxied } : {}),
      ...(values.comment !== undefined && values.comment !== ""
        ? { comment: values.comment }
        : { comment: null }),
    };
    mutations.updateMut.mutate(
      { recordId: record.id, body },
      {
        onSuccess: () => onOpenChange(false),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Editar registro</DialogTitle>
          <DialogDescription>
            Actualiza los campos permitidos por Cloudflare. El identificador del registro es{" "}
            <span className="font-mono text-xs">{record?.id}</span>.
          </DialogDescription>
        </DialogHeader>
        {record && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Input {...register("type")} />
              </div>
              <div className="space-y-2">
                <Label>TTL</Label>
                <Input type="number" {...register("ttl")} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input {...register("name")} />
            </div>
            <div className="space-y-2">
              <Label>Contenido</Label>
              <Input {...register("content")} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="dns-edit-px" {...register("proxied")} />
              <Label htmlFor="dns-edit-px">Proxied</Label>
            </div>
            <div className="space-y-2">
              <Label>Comentario</Label>
              <Input {...register("comment")} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={mutations.updateMut.isPending}>
                {mutations.updateMut.isPending ? "Guardando…" : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
